import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { isRemoteControlExcluded, participantColor } from '@srm/screen-sharing/light';
import { UiProvider } from '@srm/ui/provider';
import SupportChat from '@molecules/SupportChat';
import { useScreenShareContext } from './ScreenShareContext';
import { lastReadOwnKey, SUPPORT_CHAT_TOGGLE_ATTRIBUTE } from './supportChat';
import { supportOverlayRoot } from './supportPresence';

/** Колесо и касание в панели — только ей: блокировка прокрутки открытой модалки слушает их на `document`. */
const SCROLL_EVENTS = ['wheel', 'touchstart', 'touchmove'] as const;

/**
 * Панель чата показа справа внизу (IMPLEMENTATION.md §30): в узле оверлеев поддержки — вне захвата, поэтому в видео
 * специалиста не попадает и страницу ему не закрывает; помечена `data-remote-control-exclude`. Пока панель открыта
 * и вкладка видна, пришедшие сообщения отмечаются прочитанными. Набранный текст живёт в провайдере: свёрнутая панель
 * его не теряет. Во время управления свёрнутая панель возвращает фокус туда, где он был на странице: ввод сотрудника
 * идёт в элемент с фокусом, а поле чата и кнопки полосы ему недоступны.
 */
export default function SupportChatDock() {
    const share = useScreenShareContext();
    const { active, chatOpened, setChatOpened, markChatRead } = share;
    const opened = active && chatOpened;
    const unread = share.chat.unread;
    const dock = useRef<HTMLDivElement>(null);
    const draft = useSyncExternalStore(share.chatDraft.subscribe, share.chatDraft.get);
    const controlled = active && share.control.phase === 'granted';

    // Последний элемент страницы с фокусом во время управления — в него печатает сотрудник (§9): после чата — туда же.
    const pageFocus = useRef<HTMLElement | null>(null);
    useEffect(() => {
        if (!controlled) {
            pageFocus.current = null;
            return undefined;
        }
        const remember = (event: FocusEvent) => {
            if (event.target instanceof HTMLElement && !isRemoteControlExcluded(event.target))
                pageFocus.current = event.target;
        };
        if (document.activeElement instanceof HTMLElement && !isRemoteControlExcluded(document.activeElement))
            pageFocus.current = document.activeElement;
        document.addEventListener('focusin', remember, true);
        return () => document.removeEventListener('focusin', remember, true);
    }, [controlled]);

    useEffect(() => {
        if (!opened || !unread) return undefined;
        const read = () => {
            if (document.visibilityState === 'visible') markChatRead();
        };
        read();
        document.addEventListener('visibilitychange', read);
        return () => document.removeEventListener('visibilitychange', read);
    }, [opened, unread, markChatRead]);

    // Открытая модалка глушит прокрутку вне себя (`react-remove-scroll` на `document`), а панель — вне `body`: её ленту
    // иначе не прокрутить колесом и пальцем.
    useEffect(() => {
        const node = dock.current;
        if (!opened || !node) return undefined;
        const keep = (event: Event) => event.stopPropagation();
        SCROLL_EVENTS.forEach((type) => node.addEventListener(type, keep, { passive: true }));
        return () => SCROLL_EVENTS.forEach((type) => node.removeEventListener(type, keep));
    }, [opened]);

    const people = useMemo(
        () =>
            share.participants.map((person) => ({
                name: person.name,
                color: participantColor(person.color),
                ...(person.userId ? { userKey: person.userId } : {}),
            })),
        [share.participants],
    );
    // Панель свернули (Escape, «Свернуть», кнопка «Чат») во время управления — фокус обратно на страницу.
    const wasOpened = useRef(opened);
    useEffect(() => {
        const closed = wasOpened.current && !opened;
        wasOpened.current = opened;
        const back = pageFocus.current;
        if (closed && controlled && back?.isConnected) back.focus({ preventScroll: true });
    }, [opened, controlled]);
    const close = useCallback(() => {
        setChatOpened(false);
        if (!pageFocus.current?.isConnected)
            document.querySelector<HTMLElement>(`[${SUPPORT_CHAT_TOGGLE_ATTRIBUTE}]`)?.focus();
    }, [setChatOpened]);
    if (!opened) return null;

    return createPortal(
        <div className="support-chat-dock" ref={dock} data-remote-control-exclude>
            {/* Карточки сотрудников — внутри панели: слой оверлеев выше страницы и не ловит мышь. */}
            <UiProvider portalTarget={dock}>
                <SupportChat
                    messages={share.chat.messages}
                    typing={share.chat.typing}
                    readKey={lastReadOwnKey(share.chat)}
                    people={people}
                    waiting={!share.participants.length && !share.viewerName}
                    draft={draft}
                    onDraftChange={share.chatDraft.set}
                    onSend={share.sendChat}
                    onRetry={share.retryChat}
                    onTyping={share.chatTyping}
                    onClose={close}
                    autoFocus
                />
            </UiProvider>
        </div>,
        supportOverlayRoot(),
    );
}
